<template>
  <div
    id="sidebar"
    class="sidebar is-lavender-design"
    :class="{
      'sidebar--drawer-collapsed': isCollapsed,
      'sidebar--sub-merchant-expanded': showSubMerchantAccounts,
      'sidebar--onboarding': shouldDisplaySidebarOnboarding,
    }"
    :style="{
      '--sidebar-footer-height': `${sidebarFooterHeight}px`,
    }"
  >
    <el-menu
      ref="sidebarMenu"
      :default-active="activeMenu"
      :background-color="variables.menuBg"
      :text-color="variables.menuText"
      :unique-opened="false"
      :active-text-color="variables.menuActiveText"
      mode="vertical"
      :collapse="isCollapsed"
      :collapse-transition="false"
      :menu-trigger="'click'"
      :class="isCollapsed ? 'collapsed-menu' : ''"
      @item-click="handleClickedOnItem"
      @submenu-click="handleClickedSubmenu"
    >
      <SidebarMerchantAccountList
        v-if="subMerchantAccounts && !isKycUser"
        :visible.sync="showSubMerchantAccounts"
        :direction="currentDirection"
        :sub-merchants-search-text.sync="subMerchantsSearchText"
        :selected-merchant="selectedMerchant"
        :sub-merchants="subMerchants"
        :sub-merchant-accounts="subMerchantAccounts"
        @select="handleSelectSubMerchant"
        @open="handleBeforeOpenDrawer"
        @closed="closeDrawer"
      />

      <div class="sidebar__header">
        <SidebarHeader
          :current-locale="currentLocale"
          :collapse-button-visible="!shouldDisplaySidebarOnboarding"
          :is-collapsed="isCollapsed"
          :is-right-to-left-direction="isRightToLeftDirection"
          @click:collapse="handleCollapse"
          @click:expand="handleExpand"
        />
        <SidebarMerchantAccountSelected
          v-if="!shouldDisplaySidebarOnboarding"
          :show-sub-merchant-accounts="showSubMerchantAccounts"
          :merchant-profile="merchantProfile"
          :sub-merchants="subMerchants"
          :sub-merchant-accounts="subMerchantAccounts"
          :is-collapsed="isCollapsed"
          :active-sub-merchant-name="activeSubMerchantName"
          @click="toggleMerchantAccountDrawer"
        />
      </div>

      <div class="sidebar__inner">
        <SidebarOnboarding
          v-if="shouldDisplaySidebarOnboarding"
          :kyc-routes="kycRoutes"
          :onboarding-data="onboardingData"
        />
        <SidebarNormal
          v-else
          :show-sub-merchant-account="showSubMerchantAccounts"
          :merchant-profile="merchantProfile"
          :sub-merchant-accounts="subMerchantAccounts"
          :sub-merchants="subMerchants"
          :is-collapsed="isCollapsed"
          :permission-routes="permissionRoutes"
          :active-sub-merchant-name="activeSubMerchantName"
          @click:arrow="handleArrowClick"
          @click="closeDrawer"
        />

        <div class="divider-wrapper">
          <div class="divider" />
        </div>
      </div>
      <div
        v-if="isEnableSupportWidget"
        ref="sidebarFooter"
        class="sidebar__footer"
      >
        <BasePopover placement="right" trigger="hover" :disabled="!isCollapsed">
          <SidebarSupportWidget />
          <div slot="reference" class="sidebar__footer__support-widget-trigger">
            <svg-icon name="support" size="20px" />
          </div>
        </BasePopover>
      </div>
    </el-menu>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

import SidebarOnboarding from './_SidebarOnboarding.vue'
import SidebarNormal from './_SidebarNormal.vue'

import SidebarItem from '@/components/SidebarItem'
import variables from '@/styles/variables.scss'
import { debounce, ellipsisEnd } from '@/utils/index.js'
import userHasPermission from '@/utils/user-has-permission'
import {
  OPEN_STATUS,
  UNDER_REVIEW_STATUS,
} from '@/views/dispute/constants/status-options'
import ResolvePathMixin from '@/mixins/resolve-path'
import { findRouteByPath } from '@/utils/route'
import SidebarHeader from '@/components/SidebarUtils/header.vue'
import SidebarMerchantAccountList from '@/components/SidebarUtils/MerchantAccountList.vue'
import { getLocalizedValue } from '@/utils/i18n'
import SidebarMerchantAccountSelected from '@/components/SidebarUtils/MerchantAccountSelected.vue'
import TrackingEvent from '@/plugins/tracking/event'
import SidebarSupportWidget from '@/components/SidebarSupportWidget/SidebarSupportWidget.vue'
import BasePopover from '@/components/lavender/v2/BasePopover.vue'

const SIDEBAR_COUNT_REFRESH_ROUTES = [
  'order-list',
  'dispute-list',
  'order-detail',
  'dispute-detail',
]

Vue.component('SidebarItem', SidebarItem)

export default {
  name: 'DefaultSidebar',
  components: {
    SidebarMerchantAccountSelected,
    SidebarNormal,
    SidebarOnboarding,
    SidebarHeader,
    SidebarMerchantAccountList,
    SidebarSupportWidget,
    BasePopover,
  },
  mixins: [ResolvePathMixin],
  props: {
    showDisputeDot: {
      type: Boolean,
      default: false,
    },
    showOrderDot: {
      type: Boolean,
      default: false,
    },
    isCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSubMerchantAccounts: false,
      subMerchantsSearchText: '',
      finalSubMerchantsSearchText: '',
      sidebarFooterHeight: 0,
    }
  },
  computed: {
    ...mapGetters(['permissionRoutes', 'token']),
    ...mapGetters('order', { list: 'getListForBanner' }),
    ...mapGetters('userProfile', { userProfile: 'profile' }),
    ...mapGetters('userProfile', ['subMerchants', 'selectedMerchant']),
    ...mapGetters('profile', { merchantProfile: 'profile' }),
    ...mapGetters('translations', [
      'currentLocale',
      'currentDirection',
      'isRightToLeftDirection',
    ]),
    ...mapGetters('kyc', ['onboardingData', 'fetchingOnboardingData']),
    kycRoutes() {
      const kycPaths = []

      return this.permissionRoutes.filter((route) => {
        const isKyc = route.meta && route.meta.isKyc
        const isUnique = !kycPaths.includes(route.path)

        kycPaths.push(route.path)

        return isKyc && isUnique
      })
    },
    isKycUser() {
      return this.userProfile && this.userProfile.role === 'MERCHANT_KYC_USER'
    },
    shouldDisplaySidebarOnboarding() {
      return this.isKycUser && this.onboardingData
    },
    userCanViewOrderCount() {
      return this.merchantProfile && userHasPermission(['order_view_list_v2'])
    },
    userCanViewDisputes() {
      return this.merchantProfile && userHasPermission(['get_dispute_listing'])
    },
    activeMenu() {
      const route = this.$route
      const { meta, path, query } = route

      // If there's an active menu set in the metadata, return it
      if (meta.activeMenu) {
        return meta.activeMenu
      }

      // If we're not on any subpage such as under review orders or open disputes, return the path as is
      if (!query.statusPage) {
        return path
      }

      let statusParam = ''
      let statusValue = ''

      // If the path includes '/order/', set the status parameter and value for orders
      if (path.includes('/order/')) {
        statusParam = 'status[eq]'
        statusValue =
          query.statusPage === 'toCapture' ? 'authorised' : 'approved'
      }

      // If the path includes '/dispute/', set the status parameter and value for disputes
      if (path.includes('/dispute/')) {
        statusParam = 'statuses'
        statusValue =
          query.statusPage === 'action-required'
            ? OPEN_STATUS
            : UNDER_REVIEW_STATUS
      }

      const queryString = `${statusParam}=${statusValue}&statusPage=${query.statusPage}`

      return `${path}?${queryString}`
    },
    variables() {
      return variables
    },
    merchantName() {
      const { merchantName, arMerchantName } = this.merchantProfile
      const enName = merchantName || arMerchantName
      const arName = arMerchantName || merchantName

      return getLocalizedValue(enName, arName)
    },
    activeSubMerchantName() {
      const merchant = this.subMerchants.find(
        ({ merchantId }) => merchantId === this.selectedMerchant
      )

      if (!merchant) {
        return this.merchantName
      }

      return this.getLocalizedSubMerchantName(merchant)
    },
    subMerchantAccounts() {
      if (this.subMerchants.length <= 1) {
        return []
      }

      if (this.finalSubMerchantsSearchText) {
        const searchTextLow = this.finalSubMerchantsSearchText.toLowerCase()

        return this.subMerchants.filter((item) => {
          const merchantName = getLocalizedValue(
            item.merchantName,
            item.arMerchantName
          )
          const foundMerchantName = merchantName
            .toLowerCase()
            .includes(searchTextLow)
          const foundMerchantCode = item.merchantCode
            .toLowerCase()
            .includes(searchTextLow)

          return foundMerchantName || foundMerchantCode
        })
      }

      return this.subMerchants
    },
    isEnableSupportWidget() {
      return (
        this.$abtProvider.isFeatureEnabled('merchant_enabled_support_widget') ||
        false
      )
    },
  },
  watch: {
    subMerchantsSearchText: debounce(function(newValue) {
      this.finalSubMerchantsSearchText = newValue
    }, 500),
    '$route.name': debounce(function(routeName) {
      if (SIDEBAR_COUNT_REFRESH_ROUTES.includes(routeName)) {
        this.fetchSidebarCounts()
      }
    }, 100),
    userCanViewOrderCount(canView) {
      if (canView) {
        this.fetchOrdersCount()
      }
    },
    userCanViewDisputes(canView) {
      if (canView) {
        this.fetchDisputeSidebarCount()
      }
    },
  },
  created() {
    this.fetchSidebarCounts()
  },
  mounted() {
    this.calculateSidebarFooterHeight()

    window.addEventListener('resize', this.calculateSidebarFooterHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateSidebarFooterHeight)
  },
  methods: {
    ellipsisEnd,
    ...mapActions('userProfile', ['updateSelectedMerchant']),
    ...mapActions('order', ['fetchOrdersRecordsCount']),
    ...mapActions('dispute', ['fetchDisputeSidebarCount']),
    fetchSidebarCounts() {
      if (this.userCanViewOrderCount) {
        this.fetchOrdersCount()
      }

      if (this.userCanViewDisputes) {
        this.fetchDisputeSidebarCount()
      }
    },
    handleCollapse() {
      this.closeDrawer()
      this.$track.event(TrackingEvent.global_navbar_minimize_clicked)

      this.$emit('update:isCollapsed', true)

      setTimeout(() => {
        this.calculateSidebarFooterHeight()
      }, 100)
    },
    handleExpand() {
      this.closeDrawer()

      this.$track.event(TrackingEvent.global_navbar_maximize_clicked)
      this.$emit('update:isCollapsed', false)

      setTimeout(() => {
        this.calculateSidebarFooterHeight()
      }, 100)
    },
    fetchOrdersCount() {
      this.fetchOrdersRecordsCount()
    },
    async handleSelectSubMerchant(merchantId) {
      this.closeDrawer()

      await this.removeFilterParams()

      if (merchantId !== this.selectedMerchant) {
        this.updateSelectedMerchant(merchantId)
      }
    },
    removeFilterParams() {
      // this is clear default query filters
      // associated with specific page
      return new Promise((resolve, reject) => {
        const currentRouteName = this.$route.name
        const currentQueryParams = Object.assign({}, this.$route.query)

        if (currentRouteName === 'dashboard-home') {
          delete currentQueryParams['currency[eq]']

          return this.$router.replace(
            { query: currentQueryParams },
            resolve,
            reject
          )
        }

        if (currentRouteName === 'settlement') {
          // in settlement page we are resetting both currency and country
          delete currentQueryParams['currency[eq]']
          delete currentQueryParams['country[eq]']

          return this.$router.replace(
            { query: currentQueryParams },
            resolve,
            reject
          )
        }

        resolve()
      })
    },
    getLocalizedSubMerchantName(item) {
      return getLocalizedValue(item.merchantName, item.arMerchantName)
    },
    getTopMenuOffset() {
      const element = document.querySelector('#sidebar')

      if (!element) {
        console.error('Element #sidebar not found')
        return 0
      }

      const rect = element.getBoundingClientRect()

      return rect.top
    },
    handleBeforeOpenDrawer() {
      const offsetTop = this.getTopMenuOffset()
      // fix the issue of the drawer not showing up correctly when the page is scrolled down
      const drawerContainer = document.querySelector(
        '#sidebar .el-drawer__container'
      )
      if (offsetTop > 0) {
        drawerContainer.style.setProperty('top', `${offsetTop}px`, 'important')
      } else {
        drawerContainer.style.setProperty('top', `${0}px`, 'important')
      }
    },
    clearMerchantAccountSearchText() {
      this.subMerchantsSearchText = ''
      this.finalSubMerchantsSearchText = ''
    },
    closeDrawer() {
      if (!this.showSubMerchantAccounts) {
        return
      }

      this.showSubMerchantAccounts = false

      this.clearMerchantAccountSearchText()
    },
    toggleMerchantAccountDrawer() {
      this.$track.event(TrackingEvent.global_navbar_accounts_header_clicked)

      if (!this.subMerchantAccounts.length) {
        this.showSubMerchantAccounts = false

        return
      }

      this.showSubMerchantAccounts = !this.showSubMerchantAccounts

      this.clearMerchantAccountSearchText()
    },
    handleArrowClick(subMenuRef, targetIndex) {
      this.closeDrawer()

      const sidebarMenu = this.$refs.sidebarMenu

      if (!sidebarMenu || !subMenuRef) {
        return
      }

      const isActive = subMenuRef.active || false

      // We ignore action if sub-menu is active
      if (isActive) {
        return
      }

      const isOpening = subMenuRef.opened || false

      this.handleToggleSubmenu(sidebarMenu, targetIndex, isOpening)
    },
    handleToggleSubmenu(sidebarMenu, targetIndex, isOpening) {
      if (isOpening) {
        this.handleCollapseSubmenu(sidebarMenu, targetIndex)

        return
      }

      this.handleExpandSubmenu(sidebarMenu, targetIndex)
    },
    handleExpandSubmenu(sidebarMenu, targetIndex) {
      this.handleCollapseOtherSubmenu(sidebarMenu, targetIndex)
      sidebarMenu.open(targetIndex)
    },
    handleCollapseSubmenu(sidebarMenu, targetIndex) {
      sidebarMenu.close(targetIndex)
    },
    handleCollapseOtherSubmenu(sidebarMenu, targetIndex) {
      const submenus = sidebarMenu?.submenus || {}

      Object.values(submenus).forEach((submenu) => {
        if (
          submenu.index !== targetIndex &&
          !submenu.active &&
          submenu.opened
        ) {
          this.handleCollapseSubmenu(sidebarMenu, submenu.index)
        }
      })
    },
    handleOpenSubmenuChild(targetIndex, onComplete) {
      const matchedRoute = this.permissionRoutes.find(
        (route) =>
          this.resolvePathWithBasePath(route.path, route.path) === targetIndex
      )

      const foundAvailableRoute = findRouteByPath(
        this.permissionRoutes,
        matchedRoute?.redirect
      )

      if (foundAvailableRoute) {
        this.$router.replace({ path: matchedRoute.redirect }, onComplete)

        return true
      }

      if (!Array.isArray(matchedRoute?.children)) {
        return false
      }

      // Find the first valid non-hidden route.
      // We don't need to check permissions because matchedRoute is a filtered list from permissionRoutes
      const firstAvailableRoute = matchedRoute.children.find(
        (route) => !route.hidden
      )

      if (firstAvailableRoute) {
        this.$router.replace(firstAvailableRoute.path, onComplete)

        return true
      }

      return false
    },
    handleClickedOnItem(item) {
      const { indexPath } = item
      const [level1Item] = indexPath || []
      const sidebarMenu = this.$refs.sidebarMenu

      this.trackNavigationEventByPath(item.index)

      // A small hack to wait for the currently active submenu to turn off and turn on at the new targetIndex
      // We do this because we want to wait for the active state to be passed to targetIndex
      // This case applies when there is an active submenu but the user clicks on a menu item (single item/item in submenu)
      setTimeout(() => {
        this.handleCollapseOtherSubmenu(sidebarMenu, level1Item)
      }, 50)
    },
    handleClickedSubmenu(item) {
      const { index: targetIndex } = item
      const sidebarMenu = this.$refs.sidebarMenu

      this.trackNavigationEventByPath(item.index)

      if (item.active && item.opened) {
        // A small hack to re-open the active submenu when we click on active submenu title.
        // We do this because element-ui does not support to cancel event from `submenu-click`
        setTimeout(() => {
          sidebarMenu.open(targetIndex)
        }, 0)

        return
      }

      const handleCollapseOtherSubmenuEvent = () => {
        // A small hack to wait for the currently active submenu to turn off and turn on at the new targetIndex
        // We do this because we want to wait for the active state to be passed to targetIndex
        // This case applies when there is an active submenu but the user clicks on another submenu
        setTimeout(() => {
          this.handleCollapseOtherSubmenu(sidebarMenu, targetIndex)
        }, 50)
      }

      // If we can redirect to new URL, we wait for the redirection to complete
      const redirecting = this.handleOpenSubmenuChild(
        targetIndex,
        handleCollapseOtherSubmenuEvent
      )

      // If we cannot redirect to new URL. We collapse them as fallback case
      if (!redirecting) {
        handleCollapseOtherSubmenuEvent()
      }
    },
    trackNavigationEventByPath(targetPath) {
      const foundRoute = findRouteByPath(this.permissionRoutes, targetPath)

      if (foundRoute?.meta?.trackEvent) {
        this.$track.event(foundRoute.meta.trackEvent)
      }

      if (foundRoute?.redirect) {
        return this.trackNavigationEventByPath(foundRoute.redirect)
      }
    },
    calculateSidebarFooterHeight() {
      const sidebarFooterHeight = this.$refs.sidebarFooter?.clientHeight || 0

      this.sidebarFooterHeight = Math.min(sidebarFooterHeight, 292)
    },
  },
}
</script>

<style lang="scss" scoped>
.sidebar {
  --sidebar-header-height: 172px;
  --sidebar-footer-height: 0;
  --sidebar-body-overlap: 30px;

  width: var(--sidebar-width);

  .sidebar__header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px 16px 46px 16px;
    background: radial-gradient(
      328.14% 211.57% at 51.65% -22.19%,
      #6900a9 0%,
      #9600f1 42.27%,
      #fa3aab 100%
    );
  }

  .sidebar__inner {
    width: 100%;
    height: calc(
      100vh - var(--sidebar-header-height) - var(--sidebar-footer-height) +
        var(--sidebar-body-overlap)
    );
    padding-top: 16px;
    padding-bottom: 24px;
    margin-top: -30px;
    overflow-y: auto;
    background-color: var(--tamara-color-background-neutral-low);
    border-radius: 16px 16px 0 0;
  }

  ::v-deep {
    .el-menu .v-modal {
      display: none;
    }
  }

  &.sidebar--drawer-collapsed {
    --sidebar-header-height: 142px;
  }

  &.sidebar--sub-merchant-expanded {
    ::v-deep {
      .el-menu .v-modal {
        display: block;
        background-color: transparent;
        opacity: 0;
      }
    }
  }

  &__footer {
    width: 100%;
    padding: 16px;

    &__support-widget-trigger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 38px;
      height: 38px;
      cursor: pointer;
      background-color: rgba(0, 0, 0, 0.08);
      border-radius: var(--tamara-border-radius-sm);
    }
  }
}
</style>
